import { breakpoints, Button, Dialog, Inline, Stack } from '@etg/wings';
import { css } from '@eti/styles';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from '@eti/providers';
import { capitalize } from '../utils';
import RadioSelection from './common/RadioSelection';
import SelectBoxSelection from './common/SelectBoxSelection';
import { DebugButton } from './index';

const dialogStyles = css`
  min-height: 20rem;
`;

const loginOrderNumberOptions = [
  { label: 'DEV007 - Processing (pending)', value: 'DEV007' },
  { label: 'DEV001 - Initial (rebooking in progress)', value: 'DEV001' },
  { label: 'DEV003 - Canceled', value: 'DEV003' },
  { label: 'DEV005 - Changed', value: 'DEV005' },
  { label: 'None (social login, no selected trip)', value: 'NONE' },
  { label: 'Random (default behavior)', value: 'RANDOM' },
];

const priceLockStateOptions = [
  { label: 'None (no price lock order)', value: 'NONE' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Active, expires soon', value: 'ACTIVE_EXPIRES_SOON' },
  { label: 'Used', value: 'USED' },
  { label: 'Claimed', value: 'CLAIMED' },
  { label: 'Expired', value: 'EXPIRED' },
  { label: 'Refund', value: 'REFUND' },
  { label: 'All states (one order each)', value: 'ALL' },
];

const priceLockCountOptions = [0, 1, 2, 3, 4, 5].map((count) => ({
  label: String(count),
  value: String(count),
}));

const priceLockPastGracePeriodOptions = [
  { label: 'No: within the grace period, so in the Price Lock section', value: 'no' },
  { label: 'Yes: on the Past price locks page', value: 'yes' },
];

const priceLockSectionOptions = [
  { label: 'On', value: 'yes' },
  { label: 'Off', value: 'no' },
];

const tripCountOptions = [
  { label: 'Default mock orders', value: 'DEFAULT' },
  ...[0, 1, 2, 3, 4, 5].map((count) => ({ label: String(count), value: String(count) })),
];

const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
  const formData = new FormData(event.currentTarget);
  const formEntries = Array.from(formData.entries());

  for (const [key, value] of formEntries) {
    sessionStorage.setItem(`mocked${capitalize(key)}`, String(value));
  }
};

const DebugMyBookingsPage = () => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <DebugButton
        data-testid="my-bookings-properties-button"
        onClick={() => {
          setIsDialogOpen(true);
        }}
      >
        My Bookings properties
      </DebugButton>
      <Dialog
        closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
        footer={
          <Inline align="end" collapseBelow={breakpoints._560}>
            <Button
              data-testid="my-bookings-properties-save-button"
              form="myBookingsPropertiesConfigForm"
              type="submit"
              variant="primary"
            >
              Save
            </Button>
          </Inline>
        }
        isOpen={isDialogOpen}
        onDismiss={() => {
          setIsDialogOpen(false);
        }}
        title="My Bookings properties"
      >
        <form
          action="/debug.mybookingsproperties.mock.config"
          className={dialogStyles}
          id="myBookingsPropertiesConfigForm"
          method="POST"
          onSubmit={handleSubmit}
        >
          <Stack>
            <RadioSelection
              defaultValue="yes"
              id="isTripHistoryEnabled"
              label="Enable the Your trips layout and Trip history page"
            />
            <RadioSelection
              defaultValue="yes"
              id="isSelectedTripEnabled"
              label="Enable the Your selected trip section"
            />
            <SelectBoxSelection
              data-testid="upcomingTripsCount-dropdown"
              defaultValue={tripCountOptions[0].value}
              label="Number of upcoming trips"
              name="upcomingTripsCount"
              options={tripCountOptions}
              placeholder="Please select…"
            />
            <SelectBoxSelection
              data-testid="pastTripsCount-dropdown"
              defaultValue={tripCountOptions[0].value}
              label="Number of past trips (Trip history/Archived trips)"
              name="pastTripsCount"
              options={tripCountOptions}
              placeholder="Please select…"
            />
            <SelectBoxSelection
              data-testid="loginOrderNumber-dropdown"
              defaultValue={loginOrderNumberOptions[0].value}
              label="Selected trip (logged-in order)"
              name="loginOrderNumber"
              options={loginOrderNumberOptions}
              placeholder="Please select…"
            />
            <SelectBoxSelection
              data-testid="myBookingsPriceLockState-dropdown"
              defaultValue={priceLockStateOptions[0].value}
              label="Price lock order status"
              name="myBookingsPriceLockState"
              options={priceLockStateOptions}
              placeholder="Please select…"
            />
            <SelectBoxSelection
              data-testid="myBookingsPriceLockCount-dropdown"
              defaultValue={priceLockCountOptions[1].value}
              label="Price lock orders per status"
              name="myBookingsPriceLockCount"
              options={priceLockCountOptions}
              placeholder="Please select…"
            />
            <SelectBoxSelection
              data-testid="myBookingsPriceLockPastGracePeriod-dropdown"
              defaultValue={priceLockPastGracePeriodOptions[0].value}
              label="Past the grace period (all but Active)"
              name="myBookingsPriceLockPastGracePeriod"
              options={priceLockPastGracePeriodOptions}
              placeholder="Please select…"
            />
            <SelectBoxSelection
              data-testid="myBookingsPriceLockSectionEnabled-dropdown"
              defaultValue={priceLockSectionOptions[0].value}
              label="Price lock section (MyBookingsWelcomePage.PriceLock.Section.Enabled)"
              name="myBookingsPriceLockSectionEnabled"
              options={priceLockSectionOptions}
              placeholder="Please select…"
            />
          </Stack>
        </form>
      </Dialog>
    </>
  );
};

export default DebugMyBookingsPage;
